/**
 * PGLP | MONTE HERMOM
 * COMPONENTE: RequerenteIdentificacao
 * CASCATA: Tribunal → UF → Comarca → Fórum → Unidade
 * TEMA: Premium Dark compacto
 */

import React, { useEffect, useState } from 'react';
import {
    Gavel, Scale, FileText, Landmark, Target,
    Loader2, Info
} from 'lucide-react';

export default function RequerenteIdentificacao({ requerente, setRequerente, onNext }) {
    const [tribunais, setTribunais] = useState([]);
    const [ufs, setUfs]             = useState([]);
    const [comarcas, setComarcas]   = useState([]);
    const [foruns, setForuns]       = useState([]);
    const [unidades, setUnidades]   = useState([]);
    const [finalidades, setFinalidades] = useState([]);
    const [loading, setLoading]     = useState(true);
    const [erro, setErro]           = useState(null);

    useEffect(() => {
        Promise.all([
            fetch('/api/judiciario/cascata.php?action=tribunais', { credentials: 'include' }).then(r => r.json()),
            fetch('/api/finalidades/listar.php',                  { credentials: 'include' }).then(r => r.json())
        ])
            .then(([t, f]) => {
                if (t.success) {
                    setTribunais(t.tribunais || []);
                    if (t.tribunais?.length === 1) carregarComarcas(t.tribunais[0].id);
                }
                if (f.success) setFinalidades(f.finalidades || []);
            })
            .catch(err => setErro(err.message))
            .finally(() => setLoading(false));
    }, []);

    const carregarComarcas = (tribunal_id, uf = '') => {
        const url = `/api/judiciario/cascata.php?action=comarcas&tribunal_id=${tribunal_id}` +
                    (uf ? `&uf=${encodeURIComponent(uf)}` : '');
        fetch(url, { credentials: 'include' })
            .then(r => r.json())
            .then(d => {
                if (d.success) {
                    setComarcas(d.comarcas || []);
                    setUfs(d.ufs || []);
                }
            });
    };

    const carregarForuns = (comarca_id) => {
        fetch(`/api/judiciario/cascata.php?action=foruns&comarca_id=${comarca_id}`, { credentials: 'include' })
            .then(r => r.json())
            .then(d => { if (d.success) setForuns(d.foruns || []); });
    };

    const carregarUnidades = (comarca_id, forum_id = 0) => {
        const url = `/api/judiciario/cascata.php?action=unidades&comarca_id=${comarca_id}` +
                    (forum_id ? `&forum_id=${forum_id}` : '');
        fetch(url, { credentials: 'include' })
            .then(r => r.json())
            .then(d => { if (d.success) setUnidades(d.unidades || []); });
    };

    const handleTribunal = (id) => {
        const t = tribunais.find(x => String(x.id) === String(id));
        setRequerente(prev => ({
            ...prev,
            tribunal_id: t?.id || null, tribunal_nome: t?.sigla || '',
            uf: '', comarca_id: null, comarca_nome: '',
            forum: '', unidade_id: null, unidade_nome: ''
        }));
        setComarcas([]); setForuns([]); setUnidades([]);
        if (id) carregarComarcas(id);
    };

    const handleUf = (uf) => {
        setRequerente(prev => ({
            ...prev,
            uf, comarca_id: null, comarca_nome: '',
            forum: '', unidade_id: null, unidade_nome: ''
        }));
        setForuns([]); setUnidades([]);
        if (requerente.tribunal_id) carregarComarcas(requerente.tribunal_id, uf);
    };

    const handleComarca = (id) => {
        const c = comarcas.find(x => String(x.id) === String(id));
        setRequerente(prev => ({
            ...prev,
            comarca_id: c?.id || null,
            comarca_nome: c ? `${c.nome}/${c.uf}` : '',
            forum: '', unidade_id: null, unidade_nome: ''
        }));
        setForuns([]); setUnidades([]);
        if (id) {
            carregarForuns(id);
            carregarUnidades(id);
        }
    };

    const handleForum = (id) => {
        const f = foruns.find(x => String(x.id) === String(id));
        setRequerente(prev => ({
            ...prev,
            forum_id: f?.id || null, forum: f?.nome || '',
            unidade_id: null, unidade_nome: ''
        }));
        setUnidades([]);
        if (requerente.comarca_id) carregarUnidades(requerente.comarca_id, f?.id || 0);
    };

    const handleUnidade = (id) => {
        const u = unidades.find(x => String(x.id) === String(id));
        setRequerente(prev => ({
            ...prev,
            unidade_id: u?.id || null, unidade_nome: u?.nome || ''
        }));
    };

    const handleFinalidade = (codigo) => {
        const f = finalidades.find(x => x.codigo === codigo);
        setRequerente(prev => ({
            ...prev,
            finalidade_codigo: codigo,
            finalidade_nome:   f?.finalidade || '',
            objetivo:          f?.objetivo    || '',
            texto_laudo:       f?.texto_laudo || ''
        }));
    };

    const formatCNJ = (value) => {
        const v = value.replace(/\D/g, '').slice(0, 20);
        if (v.length > 16) return v.replace(/^(\d{7})(\d{2})(\d{4})(\d{1})(\d{2})(\d{0,4}).*/, "$1-$2.$3.$4.$5.$6");
        if (v.length > 14) return v.replace(/^(\d{7})(\d{2})(\d{4})(\d{1})(\d{0,2}).*/, "$1-$2.$3.$4.$5");
        if (v.length > 13) return v.replace(/^(\d{7})(\d{2})(\d{4})(\d{0,1}).*/, "$1-$2.$3.$4");
        if (v.length > 9)  return v.replace(/^(\d{7})(\d{2})(\d{0,4}).*/, "$1-$2.$3");
        if (v.length > 7)  return v.replace(/^(\d{7})(\d{0,2}).*/, "$1-$2");
        return v;
    };

    /* ---- ESTILOS COMPACTOS ---- */
    const inputBase =
        'w-full bg-slate-950 border border-white/10 px-3.5 py-2.5 rounded-xl text-[12px] font-bold text-white outline-none focus:border-amber-400 transition-all placeholder:text-slate-600 placeholder:font-bold';
    const selectBase = inputBase + ' cursor-pointer';
    const labelBase =
        'text-[8px] font-black text-slate-500 uppercase tracking-widest ml-3 mb-1 block';

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="animate-spin text-amber-400" size={28} />
            </div>
        );
    }
    if (erro) {
        return (
            <div className="text-center py-20">
                <p className="text-red-400 font-bold text-sm uppercase tracking-widest">⚠ {erro}</p>
            </div>
        );
    }

    const comarcaSelecionada = !!requerente.comarca_id;

    return (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">

            {/* HEADER BARRA */}
            <div className="bg-slate-900 rounded-2xl px-5 py-3.5 border border-white/5 shadow-lg relative overflow-hidden">
                <div className="relative z-10 flex items-center gap-3">
                    <div className="w-9 h-9 bg-amber-400 rounded-xl flex items-center justify-center shrink-0">
                        <Landmark size={16} className="text-slate-950" />
                    </div>
                    <div className="min-w-0 flex items-baseline gap-3 flex-wrap">
                        <h1 className="text-base font-black uppercase italic tracking-tight text-white leading-none">
                            Identificação do Juízo
                        </h1>
                        <span className="text-[8px] font-black text-amber-400 uppercase tracking-[0.25em]">
                            Tribunal → UF → Comarca → Fórum → Unidade
                        </span>
                    </div>
                </div>
                <Scale className="absolute -right-6 -bottom-6 text-white/5" size={90} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                {/* BLOCO 01 — CASCATA */}
                <div className="bg-slate-900 p-5 rounded-2xl border border-white/5 space-y-3.5">
                    <div className="flex items-center gap-2.5 border-b border-white/5 pb-2.5">
                        <Gavel className="text-amber-400" size={16} />
                        <h2 className="text-sm font-black uppercase italic text-white tracking-tight">
                            Dados do Processo
                        </h2>
                    </div>

                    <div>
                        <label className={labelBase}>Tribunal *</label>
                        <select value={requerente.tribunal_id || ''} onChange={(e) => handleTribunal(e.target.value)} className={selectBase}>
                            <option value="" className="bg-slate-900">Selecione o tribunal</option>
                            {tribunais.map(t => (
                                <option key={t.id} value={t.id} className="bg-slate-900">{t.sigla} — {t.nome}</option>
                            ))}
                        </select>
                    </div>

                    {ufs.length > 0 && (
                        <div>
                            <label className={labelBase}>UF *</label>
                            <select value={requerente.uf || ''} onChange={(e) => handleUf(e.target.value)} className={selectBase}>
                                <option value="" className="bg-slate-900">Selecione a UF</option>
                                {ufs.map(u => <option key={u} value={u} className="bg-slate-900">{u}</option>)}
                            </select>
                        </div>
                    )}

                    {comarcas.length > 0 && (
                        <div>
                            <label className={labelBase}>Comarca *</label>
                            <select value={requerente.comarca_id || ''} onChange={(e) => handleComarca(e.target.value)} className={selectBase}>
                                <option value="" className="bg-slate-900">Selecione a comarca</option>
                                {comarcas.map(c => (
                                    <option key={c.id} value={c.id} className="bg-slate-900">{c.nome}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {comarcaSelecionada && (
                        <div>
                            <div className="flex justify-between items-center ml-3 mb-1">
                                <label className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Fórum</label>
                                {foruns.length === 0 && (
                                    <span className="text-[7px] font-bold text-amber-400 uppercase tracking-widest">⚠ sem cadastro</span>
                                )}
                            </div>
                            {foruns.length === 0 ? (
                                <input
                                    value={requerente.forum || ''}
                                    onChange={(e) => setRequerente(prev => ({ ...prev, forum: e.target.value.toUpperCase() }))}
                                    placeholder="Digite o nome do fórum"
                                    className={inputBase}
                                />
                            ) : (
                                <select value={requerente.forum_id || ''} onChange={(e) => handleForum(e.target.value)} className={selectBase}>
                                    <option value="" className="bg-slate-900">Selecione o fórum</option>
                                    {foruns.map(f => <option key={f.id} value={f.id} className="bg-slate-900">{f.nome}</option>)}
                                </select>
                            )}
                        </div>
                    )}

                    {comarcaSelecionada && unidades.length > 0 && (
                        <div>
                            <label className={labelBase}>Unidade Judicial</label>
                            <select value={requerente.unidade_id || ''} onChange={(e) => handleUnidade(e.target.value)} className={selectBase}>
                                <option value="" className="bg-slate-900">Selecione a unidade</option>
                                {unidades.map(u => (
                                    <option key={u.id} value={u.id} className="bg-slate-900">
                                        {u.numero_vara ? `${u.numero_vara} — ` : ''}{u.nome}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelBase}>Vara Judicial</label>
                            <input
                                value={requerente.vara || ''}
                                onChange={(e) => setRequerente(prev => ({ ...prev, vara: e.target.value.toUpperCase() }))}
                                placeholder="Digite a vara"
                                className={inputBase}
                            />
                        </div>
                        <div>
                            <label className={labelBase}>Nº do Processo</label>
                            <input
                                value={requerente.processoJudicial || ''}
                                onChange={(e) => setRequerente(prev => ({ ...prev, processoJudicial: formatCNJ(e.target.value) }))}
                                placeholder="Digite o número do processo"
                                className={inputBase + ' font-mono'}
                            />
                        </div>
                    </div>
                </div>

                {/* BLOCO 02 — FINALIDADE */}
                <div className="bg-slate-900 p-5 rounded-2xl border border-white/5 space-y-3.5">
                    <div className="flex items-center gap-2.5 border-b border-white/5 pb-2.5">
                        <Target className="text-amber-400" size={16} />
                        <h2 className="text-sm font-black uppercase italic text-white tracking-tight">
                            Finalidade e Objetivo
                        </h2>
                    </div>

                    <div>
                        <label className={labelBase}>Finalidade (NBR 14653-1) *</label>
                        <select
                            value={requerente.finalidade_codigo || ''}
                            onChange={(e) => handleFinalidade(e.target.value)}
                            className={selectBase}
                        >
                            <option value="" className="bg-slate-900">Selecione a finalidade</option>
                            {finalidades.map(f => (
                                <option key={f.codigo} value={f.codigo} className="bg-slate-900">{f.finalidade}</option>
                            ))}
                        </select>
                    </div>

                    {requerente.objetivo && (
                        <div>
                            <div className="flex justify-between items-center ml-3 mb-1">
                                <label className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Objetivo</label>
                                <span className="text-[7px] font-bold text-amber-400 uppercase tracking-widest">✓ automático</span>
                            </div>
                            <div className="w-full bg-amber-400/10 border border-amber-400/20 px-3.5 py-2.5 rounded-xl text-[12px] font-black text-amber-400 uppercase">
                                {requerente.objetivo}
                            </div>
                        </div>
                    )}

                    {requerente.texto_laudo && (
                        <div className="p-3.5 bg-slate-950 rounded-xl border border-white/5">
                            <div className="flex items-center gap-2 mb-2">
                                <FileText size={12} className="text-slate-500" />
                                <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">
                                    Texto normativo do laudo
                                </span>
                            </div>
                            <p className="text-[10px] text-slate-400 italic leading-relaxed line-clamp-4">
                                {requerente.texto_laudo}
                            </p>
                        </div>
                    )}

                    <div className="p-3.5 bg-amber-400/5 rounded-xl border border-amber-400/20 flex gap-3">
                        <Info className="text-amber-400 flex-shrink-0 mt-0.5" size={14} />
                        <p className="text-[8px] font-bold text-amber-200/80 uppercase leading-relaxed">
                            A finalidade define o rigor estatístico da NBR 14653 e será citada automaticamente no corpo do laudo.
                        </p>
                    </div>
                </div>

            </div>

            {/* AÇÃO */}
            <div className="flex justify-end pt-1">
                <button
                    onClick={onNext}
                    disabled={!requerente.finalidade_codigo || !requerente.comarca_id || !requerente.forum}
                    className="bg-amber-400 text-slate-950 px-10 py-3.5 rounded-xl font-black text-[11px] uppercase tracking-[0.3em] hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-lg flex items-center gap-3 group"
                >
                    Consolidar Requerente
                    <FileText className="group-hover:rotate-12 transition-transform" size={16} />
                </button>
            </div>
        </div>
    );
}